/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect } from "react";
import MainLayout from "../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../assets/styles/custom-styles.css";
import useNotification from "../../../hooks/useNotification";
import Form from "../../../components/forms/Form";
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
  transformFormDataForApi,
} from "../../../utils/objectUtils";
import useBaseService from "../../../hooks/services/useBaseService";
import { entities } from "../../../constants/entities";
import PageHeader from "../../../components/PageHeader";
import SectionHeader from "../../../components/forms/SectionHeader";
import SimpleBody from "../../../components/forms/SimpleBody";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import ItemListSection from "../../../components/forms/ItemListSection";
import { Box } from "@mui/material";
import { useEventPageLogic } from "./hook/useEventPageLogic";
import EventTypeEnum from "../../../enums/event-type-enum";
import DetailsSectionRenderer from "../../../components/DetailsSectionRenderer";
import { contractDetailsFields } from "../../../constants/forms/contractFields";
import useForm from "../../../hooks/useForm";
import useQueryWithLoader from "../../../hooks/useQueryWithLoader";
import { maskCurrency } from "../../../utils/maskUtils";

const CreateEventContractPage = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { contractId } = useParams();

  const { post: baseServicePost, getByColumn: baseServiceGetByColumn } =
    useBaseService(navigate);

  const queryClient = useQueryClient();

  const { formData, formatData } = useForm(
    setDefaultFieldValues(contractDetailsFields),
  );

  // --- 1. Query para buscar os Detalhes e Infos do Contrato ---
  const { data: contractAndInfoData } = useQueryWithLoader({
    queryKey: ["contractDetails", contractId],
    queryFn: async () => {
      const [contractResponse, contractInfosResponse] = await Promise.all([
        baseServiceGetByColumn(entities.contracts.getById(contractId)),
        baseServiceGetByColumn(entities.contracts.infos(contractId)),
      ]);

      return {
        contract: contractResponse.result,
        info: contractInfosResponse.result,
      };
    },
    enabled: !!contractId,
    staleTime: 5 * 60 * 1000,
  });

  // --- useEffect para popular o formData com os detalhes do contrato ---
  useEffect(() => {
    if (contractAndInfoData) {
      formatData(
        {
          contract: contractAndInfoData.contract,
          info: {
            ...contractAndInfoData.info,
            total_amount: maskCurrency(contractAndInfoData?.info?.total_amount),
          },
        },
        contractDetailsFields,
      );
    }
  }, [contractAndInfoData, formatData]);

  const {
    form,
    event,
    aditionTerm,
    aditionQuantity,
    aditionPrice,
    aditionSupression,
    aditionModifyItem,
    aditionContractAmendment,
  } = useEventPageLogic({
    contractId,
  });

  // --- 2. Mutação para Criar Evento de Contrato (useMutation) ---
  const createEventMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await baseServicePost(
        entities.contracts.contractEventCreate(contractId),
        payload,
      );
      return response;
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        event.resetForm();
        navigate(`/aluguel/contratos`);
        queryClient.invalidateQueries(["contractDetails", contractId]);
      }
    },
    onError: (error) => {
      console.log("onError ", error);

      if (error.response?.data?.errors) {
        // Popula os erros nos campos específicos (destaca em vermelho)
        form.setErrors(error.response.data.errors);

        // Mostra a PRIMEIRA mensagem de erro em toast
        const firstError = Object.values(error.response.data.errors).flat()[0];
        if (firstError) {
          showNotification("error", firstError);
        }
      } else {
        // Erro genérico (500, 403, etc.)
        showNotification(
          "error",
          error.response?.data?.message || "Erro ao criar o evento do contrato.",
        );
      }
    },
  });

  // Função de submissão do formulário
  const handleSubmit = async () => {
    try {
      const selectedValues = event.selects.eventTypesSelect.selectedValue.map(
        (item) => item.value,
      );

      for (const value of selectedValues) {
        switch (value) {
          case 3:
            if (aditionQuantity.aditionQuantity.length < 1) {
              showNotification(
                "warning",
                "Preencha pelo menos um item de aditivo de quantidade",
              );
              return;
            }
            break;

          case 4:
            if (aditionPrice.aditionPrice.length < 1) {
              showNotification(
                "warning",
                "Preencha pelo menos um item de aditivo de reajuste",
              );
              return;
            }
            break;

          case 5:
            if (aditionSupression.aditionSupression.length < 1) {
              showNotification(
                "warning",
                "Preencha pelo menos um item de aditivo de supressão",
              );
              return;
            }
            break;
          case 6:
            if (aditionModifyItem.aditionModifyItem.length < 1) {
              showNotification(
                "warning",
                "Preencha pelo menos um item de aditivo de modificação de item",
              );
              return;
            }
            break;
          // * Não é possível aplicar validação específica para o evento de apostilamento,
          // * pois ele pode alterar itens, serviços ou informações gerais do contrato.
          default:
            break;
        }
      }

      const payload = {
        ...event.formData,
        ...aditionTerm.formData,
        aditionQuantity: aditionQuantity.aditionQuantity,
        aditionPrice: aditionPrice.aditionPrice,
        aditionSupression: aditionSupression.aditionSupression,
        aditionModifyItem: aditionModifyItem.aditionModifyItem,
        aditionContractAmendment: {
          ...aditionContractAmendment.formData.aditionContractAmendment,
          items: aditionContractAmendment.items.aditionContractAmendmentItems,
          jobs: aditionContractAmendment.jobs.aditionContractAmendmentJobs,
        },
      };

      const transformedFormData = transformFormDataForApi(payload);
      const cleanedPayload = cleanPayload(transformedFormData);

      return await createEventMutation.mutateAsync(cleanedPayload);
    } catch (error) {
      console.log("Erro ao criar evento do contrato:", {
        status: error.status,
        message: error.message,
      });
    }
  };

  const handleBack = useCallback(() => {
    navigate(`/aluguel/contratos`);
  }, [navigate]);

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Adicionar Evento ao Contrato"
        showBackButton={true}
        backUrl={`/aluguel/contratos`}
      />

      <DetailsSectionRenderer
        formData={formData}
        sections={contractDetailsFields}
      />

      <div className="container-fluid p-1">
        <Form
          onSubmit={handleSubmit}
          textSubmit="Adicionar"
          textLoadingSubmit="Adicionando..."
          handleBack={handleBack}
          isLoading={createEventMutation.isPending}>
          {() => (
            <>
              <div key={event.section} className="my-4">
                <SectionHeader
                  section={event.section}
                  description={event.description}
                />

                <SimpleBody
                  fields={event.fields}
                  formErrors={normalizeFormErrors(form.errors)}
                  formData={event.formData}
                  handleFieldChange={event.handleChange}
                  getOptions={event.getOptions}
                  getSelectedValue={event.getSelectedValue}
                  getSelectMeta={event.getSelectMeta}
                />
              </div>

              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO DE PRAZO"]) && (
                <>
                  <Box sx={{ mb: 8 }}>
                    <hr />
                  </Box>

                  <Box key={aditionTerm.section} sx={{ mb: 4 }}>
                    <SectionHeader
                      section={aditionTerm.section}
                      description={aditionTerm.description}
                    />

                    <SimpleBody
                      fields={aditionTerm.fields}
                      formErrors={normalizeFormErrors(form.errors)}
                      formData={aditionTerm.formData}
                      handleFieldChange={aditionTerm.handleChange}
                      getOptions={() => {}}
                      getSelectedValue={() => {}}
                    />
                  </Box>
                </>
              )}

              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO DE QUANTIDADE"]) && (
                <>
                  <Box sx={{ mb: 8 }}>
                    <hr />
                  </Box>

                  <Box
                    key={aditionQuantity.section}
                    sx={{
                      mt: "1rem",
                      display: "flex",
                      flexDirection: "column",
                    }}>
                    <ItemListSection
                      section={aditionQuantity.section}
                      description={aditionQuantity.description}
                      fields={aditionQuantity.fields}
                      fieldsState={aditionQuantity.fieldsState}
                      initialData={aditionQuantity.aditionQuantity}
                      onItemsChange={
                        aditionQuantity.handleAditionsQuantityChange
                      }
                      renderFormBody={
                        aditionQuantity.renderAditionQuantityFormBody
                      }
                      getItemDisplayValue={
                        aditionQuantity.getAditionQuantityDisplayValue
                      }
                      itemFormData={aditionQuantity.formData}
                      onItemChange={aditionQuantity.handleChange}
                      itemFormErrors={form.errors}
                      onFormSubmit={aditionQuantity.handleSaveAditionQuantity}
                      initializeItemForm={
                        aditionQuantity.initializeAditionQuantityForm
                      }
                      isDisabledButton={false}
                      getOptions={aditionQuantity.getOptions}
                      getSelectedValue={aditionQuantity.getSelectedValue}
                      getSelectMeta={aditionQuantity.getSelectMeta}
                      notShowHeader={["aditionQuantity.new"]}
                    />
                  </Box>
                </>
              )}

              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO DE REAJUSTE"]) && (
                <>
                  <Box sx={{ mb: 8 }}>
                    <hr />
                  </Box>

                  <Box
                    key={aditionPrice.section}
                    sx={{
                      mt: "1rem",
                      display: "flex",
                      flexDirection: "column",
                    }}>
                    <ItemListSection
                      section={aditionPrice.section}
                      description={aditionPrice.description}
                      fields={aditionPrice.fields} // ORIGINAL - para TABELA
                      fieldsState={aditionPrice.fieldsState} // DINÂMICO - para FORMULÁRIO
                      initialData={aditionPrice.aditionPrice}
                      onItemsChange={aditionPrice.handleAditionsPriceChange}
                      renderFormBody={aditionPrice.renderAditionPriceFormBody}
                      getItemDisplayValue={
                        aditionPrice.getAditionPriceDisplayValue
                      }
                      itemFormData={aditionPrice.formData}
                      onItemChange={aditionPrice.handleChange}
                      itemFormErrors={form.errors}
                      onFormSubmit={aditionPrice.handleSaveAditionPrice}
                      initializeItemForm={
                        aditionPrice.initializeAditionPriceForm
                      }
                      isDisabledButton={false}
                      getOptions={aditionPrice.getOptions}
                      getSelectedValue={aditionPrice.getSelectedValue}
                      getSelectMeta={aditionPrice.getSelectMeta}
                      notShowHeader={["aditionPrice.item_or_job"]}
                    />
                  </Box>
                </>
              )}

              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO DE SUPRESSÃO"]) && (
                <>
                  <Box sx={{ mb: 8 }}>
                    <hr />
                  </Box>

                  <Box
                    key={aditionSupression.section}
                    sx={{
                      mt: "1rem",
                      display: "flex",
                      flexDirection: "column",
                    }}>
                    <ItemListSection
                      section={aditionSupression.section}
                      description={aditionSupression.description}
                      fields={aditionSupression.fields}
                      initialData={aditionSupression.aditionSupression}
                      onItemsChange={
                        aditionSupression.handleAditionsSupressionChange
                      }
                      renderFormBody={
                        aditionSupression.renderAditionSupressionFormBody
                      }
                      getItemDisplayValue={
                        aditionSupression.getAditionSupressionDisplayValue
                      }
                      itemFormData={aditionSupression.formData}
                      onItemChange={aditionSupression.handleChange}
                      itemFormErrors={form.errors}
                      onFormSubmit={
                        aditionSupression.handleSaveAditionSupression
                      }
                      initializeItemForm={
                        aditionSupression.initializeAditionSupressionForm
                      }
                      isDisabledButton={false}
                      getOptions={aditionSupression.getOptions}
                      getSelectedValue={aditionSupression.getSelectedValue}
                      getSelectMeta={aditionSupression.getSelectMeta}
                    />
                  </Box>
                </>
              )}

              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum["ADITIVO MODIFICAÇÃO DE ITEM"]) && (
                <>
                  <Box sx={{ mb: 8 }}>
                    <hr />
                  </Box>

                  <Box
                    key={aditionModifyItem.section}
                    sx={{
                      mt: "1rem",
                      display: "flex",
                      flexDirection: "column",
                    }}>
                    <ItemListSection
                      section={aditionModifyItem.section}
                      description={aditionModifyItem.description}
                      fields={aditionModifyItem.fields}
                      initialData={aditionModifyItem.aditionModifyItem}
                      onItemsChange={
                        aditionModifyItem.handleAditionsModifyItemChange
                      }
                      renderFormBody={
                        aditionModifyItem.renderAditionModifyItemFormBody
                      }
                      getItemDisplayValue={
                        aditionModifyItem.getAditionModifyItemDisplayValue
                      }
                      itemFormData={aditionModifyItem.formData}
                      onItemChange={aditionModifyItem.handleChange}
                      itemFormErrors={form.errors}
                      onFormSubmit={
                        aditionModifyItem.handleSaveAditionModifyItem
                      }
                      initializeItemForm={
                        aditionModifyItem.initializeAditionModifyItemForm
                      }
                      isDisabledButton={false}
                      getOptions={aditionModifyItem.getOptions}
                      getSelectedValue={aditionModifyItem.getSelectedValue}
                      getSelectMeta={aditionModifyItem.getSelectMeta}
                    />
                  </Box>
                </>
              )}

              {event.selects.eventTypesSelect?.selectedValue
                ?.map((opt) => opt.value)
                .includes(EventTypeEnum.APOSTILAMENTO) && (
                <>
                  <Box sx={{ mb: 8 }}>
                    <hr />
                  </Box>

                  <div key={aditionContractAmendment.section}>
                    {/* Campos GLOBAIS (datas) */}
                    <SectionHeader
                      section={aditionContractAmendment.section}
                      description={aditionContractAmendment.description}
                    />
                    <SimpleBody
                      fields={aditionContractAmendment.fields}
                      formData={aditionContractAmendment.formData}
                      handleFieldChange={aditionContractAmendment.handleChange}
                      formErrors={normalizeFormErrors(form.errors)}
                      getOptions={aditionContractAmendment.getOptions}
                      getSelectedValue={
                        aditionContractAmendment.getSelectedValue
                      }
                      getSelectMeta={aditionContractAmendment.getSelectMeta}
                    />

                    {/* Lista de ITEMS */}
                    <Box sx={{ mt: "1rem" }}>
                      <ItemListSection
                        section={aditionContractAmendment.items.section}
                        description={aditionContractAmendment.items.description}
                        fields={aditionContractAmendment.items.fields}
                        initialData={
                          aditionContractAmendment.items
                            .aditionContractAmendmentItems
                        }
                        onItemsChange={
                          aditionContractAmendment.items
                            .setAditionContractAmendmentItems
                        }
                        renderFormBody={
                          aditionContractAmendment.items
                            .renderAditionContractAmendmentItemsFormBody
                        }
                        getItemDisplayValue={
                          aditionContractAmendment.items
                            .getAditionContractAmendmentItemsDisplayValue
                        }
                        itemFormData={aditionContractAmendment.items.formData}
                        onItemChange={
                          aditionContractAmendment.items.handleChange
                        }
                        itemFormErrors={form.errors}
                        onFormSubmit={
                          aditionContractAmendment.items
                            .handleSaveAditionContractAmendmentItems
                        }
                        initializeItemForm={
                          aditionContractAmendment.items
                            .initializeAditionContractAmendmentItemsForm
                        }
                        getOptions={aditionContractAmendment.items.getOptions}
                        getSelectedValue={
                          aditionContractAmendment.items.getSelectedValue
                        }
                        getSelectMeta={
                          aditionContractAmendment.items.getSelectMeta
                        }
                      />
                    </Box>

                    {/* Lista de JOBS */}
                    <Box sx={{ mt: "1rem" }}>
                      <ItemListSection
                        section={aditionContractAmendment.jobs.section}
                        description={aditionContractAmendment.jobs.description}
                        fields={aditionContractAmendment.jobs.fields}
                        initialData={
                          aditionContractAmendment.jobs
                            .aditionContractAmendmentJobs
                        }
                        onItemsChange={
                          aditionContractAmendment.jobs
                            .setAditionContractAmendmentJobs
                        }
                        renderFormBody={
                          aditionContractAmendment.jobs
                            .renderAditionContractAmendmentJobsFormBody
                        }
                        getItemDisplayValue={
                          aditionContractAmendment.jobs
                            .getAditionContractAmendmentJobsDisplayValue
                        }
                        itemFormData={aditionContractAmendment.jobs.formData}
                        onItemChange={
                          aditionContractAmendment.jobs.handleChange
                        }
                        itemFormErrors={form.errors}
                        onFormSubmit={
                          aditionContractAmendment.jobs
                            .handleSaveAditionContractAmendmentJobs
                        }
                        initializeItemForm={
                          aditionContractAmendment.jobs
                            .initializeAditionContractAmendmentJobsForm
                        }
                        getOptions={aditionContractAmendment.jobs.getOptions}
                        getSelectedValue={
                          aditionContractAmendment.jobs.getSelectedValue
                        }
                        getSelectMeta={
                          aditionContractAmendment.jobs.getSelectMeta
                        }
                      />
                    </Box>
                  </div>
                </>
              )}
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  );
};

export default CreateEventContractPage;
